<template>
  <page-header-wrapper>
    <a-card :bordered="false">
      <!-- 操作栏 -->
      <div class="table-operations" style="margin-bottom: 16px;">
        <a-button type="primary" @click="uploadFileDialog = true">
          <a-icon type="upload" />上传图片
        </a-button>
        <a-button style="margin-left: 8px" @click="createDirDialog = true">
          <a-icon type="folder-add" />新建目录
        </a-button>
        <a-button style="margin-left: 8px" @click="uploadFolderDialog = true">
          <a-icon type="folder-open" />上传文件夹
        </a-button>
        <a-button style="margin-left: 8px" @click="goBack" v-if="currentPath">
          <a-icon type="rollback" />返回上级
        </a-button>
        <span style="margin-left: 16px; color: #666;">
          当前路径：{{ currentPath || '/' }}
        </span>
        <a-input-search
          style="width: 300px; float: right;"
          placeholder="请输入文件名前缀"
          @search="handleSearch"
        />
      </div>

      <!-- 数据表格 -->
      <a-table
        :loading="loading"
        rowKey="filePath"
        :columns="columns"
        :data-source="fileList"
        :pagination="false"
        :bordered="true"
      >
        <!-- 目录/文件图标 -->
        <template slot="icon" slot-scope="text, record">
          <a-icon v-if="record.isDir" type="folder" style="color: #faad14; font-size: 20px;" />
          <img v-else-if="isImage(record.fileName)" :src="record.fileUrl" style="width: 24px; height: 24px; object-fit: cover;" />
          <a-icon v-else type="file" style="font-size: 20px;" />
        </template>

        <!-- 文件名 -->
        <template slot="fileName" slot-scope="text, record">
          <a @click="handleItemClick(record)" style="color: #1890ff;">
            {{ record.fileName }}
          </a>
        </template>

        <!-- 文件大小 -->
        <template slot="fileSize" slot-scope="text, record">
          {{ !record.isDir ? formatFileSize(record.fileSize) : '-' }}
        </template>
      </a-table>

      <!-- 创建目录弹窗 -->
      <a-modal title="新建目录" :visible="createDirDialog" @ok="createDir" @cancel="createDirDialog = false">
        <a-form layout="vertical">
          <a-form-item label="目录名称">
            <a-input v-model="dirForm.dirName" placeholder="请输入目录名称" />
          </a-form-item>
        </a-form>
      </a-modal>

      <!-- 多图片上传弹窗 -->
      <a-modal title="批量上传图片" :visible="uploadFileDialog" @ok="submitUploadFile" @cancel="resetFileUpload">
        <input
          ref="fileInput"
          type="file"
          style="display: none;"
          accept="image/*"
          multiple
          @change="handleManualFileChange"
        />
        <a-button @click="$refs.fileInput.click()">
          <a-icon type="upload" />选择图片（最多20张）
        </a-button>

        <div style="margin-top: 12px;">
          <div style="color: #666;margin-bottom: 8px;">
            已选择：{{ selectedFiles.length }} 张 / 最多20张
          </div>
          <!-- 图片预览 -->
          <div style="display: flex;flex-wrap: wrap;gap: 8px;">
            <div v-for="(item,idx) in previewFiles" :key="idx" style="position: relative;">
              <img :src="item" style="width:60px;height:60px;object-fit:cover;border-radius:4px;">
              <a-icon
                type="close-circle"
                theme="filled"
                style="position:absolute;top:-6px;right:-6px;color:#ff4d4f;cursor: pointer;"
                @click="removeFile(idx)"
              />
            </div>
          </div>
        </div>
      </a-modal>

      <!-- 上传文件夹弹窗 -->
      <a-modal title="上传文件夹" :visible="uploadFolderDialog" @ok="submitUploadFolder" @cancel="resetFolderUpload">
        <a-upload
          ref="folderUpload"
          :file-list="folderUploadList"
          :before-upload="handleBeforeUpload"
          :on-change="handleFolderChange"
          directory
        >
          <a-button>
            <a-icon type="folder-open" />选择文件夹
          </a-button>
          <div class="ant-upload-hint" style="margin-top: 8px;">
            支持上传整个文件夹（仅现代浏览器兼容）
          </div>
        </a-upload>
      </a-modal>

      <!-- 图片预览弹窗 -->
      <a-modal :visible="previewDialog" :footer="null" @cancel="previewDialog = false">
        <img alt="预览" style="width: 100%" :src="previewUrl" />
      </a-modal>
    </a-card>
  </page-header-wrapper>
</template>

<script>
import { listOssFiles, createOssDir, uploadObject, uploadOssFolder } from "@/api/tool/oss";

export default {
  name: "MaterialManager",
  data() {
    return {
      loading: false,
      currentPath: "",
      fileList: [],
      searchKey: "",
      // 弹窗
      createDirDialog: false,
      dirForm: { dirName: "" },
      uploadFileDialog: false,
      uploadFolderDialog: false,
      folderUploadList: [],
      previewDialog: false,
      previewUrl: "",
      // 多文件上传
      selectedFiles: [],
      previewFiles: [],
      columns: [
        {
          title: "",
          dataIndex: "icon",
          width: 60,
          scopedSlots: { customRender: "icon" },
        },
        {
          title: "文件名",
          dataIndex: "fileName",
          scopedSlots: { customRender: "fileName" },
        },
        {
          title: "大小",
          dataIndex: "fileSize",
          width: 120,
          scopedSlots: { customRender: "fileSize" },
        },
      ],
    };
  },
  created() {
    this.loadFileList();
  },
  methods: {
    async loadFileList() {
      this.loading = true;
      try {
        let list = await listOssFiles(this.currentPath);
        if (this.searchKey) {
          list = list.filter(item => item.fileName.includes(this.searchKey));
        }
        this.fileList = list;
      } catch (err) {
        this.$message.error("加载文件列表失败：" + err.message);
      } finally {
        this.loading = false;
      }
    },
    handleSearch(value) {
      this.searchKey = value;
      this.loadFileList();
    },
    handleItemClick(item) {
      if (item.isDir) {
        this.currentPath = item.filePath.slice(0, -1);
        this.loadFileList();
      } else if (this.isImage(item.fileName)) {
        this.previewUrl = item.fileUrl;
        this.previewDialog = true;
      }
    },
    goBack() {
      if (this.currentPath.includes("/")) {
        this.currentPath = this.currentPath.substring(0, this.currentPath.lastIndexOf("/"));
      } else {
        this.currentPath = "";
      }
      this.loadFileList();
    },
    async createDir() {
      if (!this.dirForm.dirName) {
        this.$message.warning("请输入目录名称");
        return;
      }
      try {
        const dirPath = this.currentPath ? `${this.currentPath}/${this.dirForm.dirName}` : this.dirForm.dirName;
        await createOssDir(dirPath);
        this.$message.success("目录创建成功");
        this.createDirDialog = false;
        this.dirForm.dirName = "";
        this.loadFileList();
      } catch (err) {
        this.$message.error("创建目录失败：" + err.message);
      }
    },

    // =============== 多图片上传核心 ===============
    handleManualFileChange(e) {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;

      // 限制最多20张
      if (files.length > 20) {
        this.$message.warning("最多只能选择20张图片");
        return;
      }

      // 仅图片
      const imageFiles = files.filter(f => this.isImage(f.name));
      if (imageFiles.length !== files.length) {
        this.$message.warning("只允许上传图片文件");
        return;
      }

      this.selectedFiles = imageFiles;
      // 生成预览
      this.previewFiles = imageFiles.map(file => URL.createObjectURL(file));
    },
    // 移除单个文件
    removeFile(index) {
      this.selectedFiles.splice(index, 1);
      this.previewFiles.splice(index, 1);
    },
    resetFileUpload() {
      this.uploadFileDialog = false;
      this.selectedFiles = [];
      this.previewFiles = [];
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    // 批量提交上传
    async submitUploadFile() {
        console.log("点击了确定，开始执行上传...")
        if (this.selectedFiles.length === 0) {
          this.$message.warning("请选择图片");
          return;
        }
        if (this.selectedFiles.length > 20) {
          this.$message.warning("最多20张");
          return;
        }

        // 正确的 Ant Design Vue loading 写法
        const loadingMsg = this.$message.loading("上传中...", 0); // 0 表示不自动关闭
        let success = 0;

        try {
          for (const file of this.selectedFiles) {
            const filePath = this.currentPath
              ? `${this.currentPath}/${file.name}`
              : file.name;
            await uploadObject(filePath, file);
            success++;
          }
          loadingMsg(); // 关闭 loading
          this.$message.success(`成功上传 ${success} 张图片`);
          this.resetFileUpload();
          setTimeout(() => this.loadFileList(), 300);
        } catch (err) {
          loadingMsg(); // 关闭 loading
          this.$message.error(`上传失败：${err.message}`);
        }
      },

    // =============== 文件夹上传 ===============
    handleBeforeUpload() {
      return false;
    },
    handleFolderChange(info) {
      this.folderUploadList = info.fileList;
    },
    resetFolderUpload() {
      this.uploadFolderDialog = false;
      this.folderUploadList = [];
      if (this.$refs.folderUpload) this.$refs.folderUpload.clearFiles();
    },
    async submitUploadFolder() {
      if (!this.folderUploadList.length) {
        this.$message.warning("请选择文件夹");
        return;
      }
      try {
        const files = this.folderUploadList.map(f => ({
          raw: f.originFileObj || f.file,
          relativePath: f.name.split("/").slice(1).join("/")
        }));
        await uploadOssFolder(files, this.currentPath);
        this.$message.success("文件夹上传成功");
        this.resetFolderUpload();
        setTimeout(() => this.loadFileList(), 300);
      } catch (err) {
        this.$message.error(`上传失败：${err.message}`);
      }
    },

    // =============== 工具方法 ===============
    isImage(fileName) {
      const ext = fileName.split(".").pop().toLowerCase();
      return ["jpg", "jpeg", "png", "gif", "bmp"].includes(ext);
    },
    formatFileSize(size) {
      if (!size) return "0 B";
      if (size < 1024) return size + " B";
      if (size < 1024 * 1024) return (size / 1024).toFixed(2) + " KB";
      return (size / (1024 * 1024)).toFixed(2) + " MB";
    },
  },
};
</script>

<style scoped>
.table-operations {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.ant-upload-hint {
  font-size: 12px;
  color: #999;
}
</style>